import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { usePostAttendanceContext } from "@/contexts/PostAttendanceContext";
import { useSessionContext } from "@/contexts/SessionContext";
import { oids } from "@/configs/Settings";
import { Assignment, WhatsApp } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormGroup,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { toast } from "react-toastify";

import { QuestionnaireProtocolLinkChips } from "@/components/common/QuestionnaireProtocolLinkChips";
import { useOrganizationProtocolQuestionnaireIndex } from "@/services/fhir/protocol/hooks/useOrganizationProtocolQuestionnaireIndex";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP } from "src-next/modules/questionnaire-builder/questionnaire-management/utils/questionnaireManagementConstants";
import { useQuestionnaireTaskSend } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";
import { useFormatter } from "@/utils/useFormatter";
import { useInternationalVerify } from "@/utils/useInternationalVerify";
import {
  getAllowedUsageTypesForScheduleQuestionnaireSend,
  isQuestionnaireAllowedForScheduleSend,
} from "@/utils/questionnaireUsageTabPolicy";

const fluxZapLinkSx = {
  color: "primary.main",
  fontWeight: 600,
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
} as const;

const FLUXMED_APP_URL = "https://app.fluxmed.com.br";

function formatPatientGreetingName(name?: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed || trimmed === "undefined") {
    return "paciente";
  }
  return useFormatter.formatNameDisplay(trimmed) || "paciente";
}

function buildQuestionnaireWhatsAppMessage(patientName?: string): string {
  const greetingName = formatPatientGreetingName(patientName);
  return (
    `Olá, ${greetingName} foi enviado um questionário para você. ` +
    `Acesse o aplicativo web em ${FLUXMED_APP_URL}, ou baixe o app Fluxmed (Google Play ou App Store) para responder o questionário.`
  );
}

function buildQuestionnaireWhatsAppUrl(
  patientName?: string,
  patientPhone?: string,
): string {
  const message = buildQuestionnaireWhatsAppMessage(patientName);
  const encodedText = encodeURIComponent(message);
  const rawPhone = patientPhone?.trim();

  if (!rawPhone) {
    return `https://web.whatsapp.com/send?text=${encodedText}`;
  }

  const phone = useInternationalVerify.verifyPhoneDDI(rawPhone);
  return `https://web.whatsapp.com/send?phone=+${phone}&text=${encodedText}`;
}

type UsageTabValue = "all" | QuestionnairePayload["usageType"];

const USAGE_TABS: { value: UsageTabValue; label: string }[] = [
  { value: "all", label: "Todos" },
  { value: "triage", label: "Triagem" },
  { value: "preConsultation", label: "Pré-consulta" },
  { value: "postConsultation", label: "Pós-consulta" },
  { value: "attendance", label: "Acompanhamento" },
];

const STANDARD_USAGE: QuestionnairePayload["usageType"][] = [
  "triage",
  "preConsultation",
  "postConsultation",
  "attendance",
];

function matchesUsageTab(
  questionnaire: QuestionnairePayload,
  tab: UsageTabValue,
): boolean {
  if (tab === "all") return true;
  return questionnaire.usageType === tab;
}

function a11yProps(index: number) {
  return {
    id: `send-q-tab-${index}`,
    "aria-controls": `send-q-tabpanel-${index}`,
  };
}

export interface SendQuestionnairesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Referência FHIR completa do paciente, ex. Patient/{id} */
  patientReference: string | null;
  /**
   * Status FHIR do Appointment. Quando omitido, mantém o comportamento anterior (todas as abas).
   */
  appointmentStatus?: string;
  /**
   * `Appointment/{id}` ou id lógico (FHIR) quando o envio estiver vinculado a este agendamento.
   */
  appointmentReference?: string;
  /**
   * Atendimento iniciado para este agendamento (ex. `inProgress === appointment.id` no contexto clínico).
   * Libera pós-consulta na fila antes de `fulfilled`.
   */
  hasActiveEncounter?: boolean;
  /** Nome do paciente para o aviso manual no WhatsApp Web. */
  patientName?: string;
  /** Telefone do paciente para abrir o chat direto no WhatsApp Web. */
  patientPhone?: string;
}

interface FormValues {
  /** IDs dos Questionnaire selecionados para envio. */
  selectedQuestionnaireIds: string[];
}

/**
 * Dialog: lista questionários da organização (src-next), filtra por abas de uso,
 * seleção múltipla com react-hook-form e cria uma Task por item no workflow FHIR.
 */
export const SendQuestionnairesDialog = memo(function SendQuestionnairesDialog({
  open,
  onClose,
  patientReference,
  appointmentStatus,
  appointmentReference,
  hasActiveEncounter,
  patientName,
  patientPhone,
}: SendQuestionnairesDialogProps) {
  const theme = useTheme();
  const { access, user } = useSessionContext();
  const { contractPostAttendance } = usePostAttendanceContext();
  const hasFluxZapExtension = !!contractPostAttendance;
  const { organizationId } = useOrganizationContext();
  const [usageTab, setUsageTab] = useState<UsageTabValue>("all");

  const scheduleAllowedUsage = useMemo(() => {
    if (appointmentStatus === undefined || appointmentStatus === "") {
      return null;
    }
    return getAllowedUsageTypesForScheduleQuestionnaireSend(appointmentStatus, {
      hasActiveEncounter,
    });
  }, [appointmentStatus, hasActiveEncounter]);

  const visibleUsageTabs = useMemo(() => {
    if (scheduleAllowedUsage === null) {
      return USAGE_TABS;
    }
    if (scheduleAllowedUsage.length === 0) {
      return [];
    }
    const tabs: { value: UsageTabValue; label: string }[] = [
      { value: "all", label: "Todos" },
    ];
    for (const row of USAGE_TABS) {
      if (row.value === "all") continue;
      if (
        STANDARD_USAGE.includes(
          row.value as QuestionnairePayload["usageType"],
        ) &&
        scheduleAllowedUsage.includes(
          row.value as QuestionnairePayload["usageType"],
        )
      ) {
        tabs.push(row);
      }
    }
    return tabs;
  }, [scheduleAllowedUsage]);

  const { status: protocolIndexStatus, getProtocolNamesForQuestionnaire } =
    useOrganizationProtocolQuestionnaireIndex({ enabled: open });

  const { questionnaires, isLoading, errorMessage } =
    useQuestionnaireManagement({
      organizationId: `${oids.cnpj}-${organizationId}`,
    });

  const { sendQuestionnaireTasks, isSending, sendError, resetSendState } =
    useQuestionnaireTaskSend();

  const { watch, getValues, setValue, handleSubmit, reset } =
    useForm<FormValues>({
      defaultValues: { selectedQuestionnaireIds: [] },
    });

  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [whatsappPromptOpen, setWhatsappPromptOpen] = useState(false);

  const selectedQuestionnaireIds = watch("selectedQuestionnaireIds");

  const filteredQuestionnaires = useMemo(() => {
    return questionnaires.filter((q) => {
      if (!isQuestionnaireSelectableForClinicalUse(q)) return false;
      if (
        scheduleAllowedUsage !== null &&
        !isQuestionnaireAllowedForScheduleSend(q, scheduleAllowedUsage)
      ) {
        return false;
      }
      return matchesUsageTab(q, usageTab);
    });
  }, [questionnaires, scheduleAllowedUsage, usageTab]);

  useEffect(() => {
    if (!open) return;
    const values = visibleUsageTabs.map((t) => t.value);
    if (values.length === 0) return;
    if (!values.includes(usageTab)) {
      setUsageTab(values[0]!);
    }
  }, [open, visibleUsageTabs, usageTab]);

  const practitionerRoleReference = useMemo(() => {
    const username = user?.username;
    if (!access?.organizationId || typeof username !== "string") return "";
    return `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`;
  }, [access?.organizationId, user?.username]);

  const scheduleAllowsSend =
    scheduleAllowedUsage === null || scheduleAllowedUsage.length > 0;

  const canSend =
    !!patientReference &&
    !!access?.organizationId &&
    access.type === "professional" &&
    !!practitionerRoleReference &&
    scheduleAllowsSend;

  const toggleQuestionnaireId = useCallback(
    (questionnaireId: string) => {
      const current = getValues("selectedQuestionnaireIds");
      const next = current.includes(questionnaireId)
        ? current.filter((id) => id !== questionnaireId)
        : [...current, questionnaireId];
      setValue("selectedQuestionnaireIds", next, { shouldValidate: true });
    },
    [getValues, setValue],
  );

  const handleClose = useCallback(() => {
    setConfirmSendOpen(false);
    setWhatsappPromptOpen(false);
    reset({ selectedQuestionnaireIds: [] });
    resetSendState();
    setUsageTab("all");
    onClose();
  }, [onClose, reset, resetSendState]);

  const handleOpenWhatsAppNotice = useCallback(() => {
    const whatsAppUrl = buildQuestionnaireWhatsAppUrl(
      patientName,
      patientPhone,
    );
    window.open(whatsAppUrl, "_blank", "noopener,noreferrer");
  }, [patientName, patientPhone]);

  const handleDismissWhatsappPrompt = useCallback(() => {
    setWhatsappPromptOpen(false);
    reset({ selectedQuestionnaireIds: [] });
    resetSendState();
    setUsageTab("all");
    onClose();
  }, [onClose, reset, resetSendState]);

  const handleSendWhatsappAndClose = useCallback(() => {
    handleOpenWhatsAppNotice();
    setWhatsappPromptOpen(false);
    reset({ selectedQuestionnaireIds: [] });
    resetSendState();
    setUsageTab("all");
    onClose();
  }, [handleOpenWhatsAppNotice, onClose, reset, resetSendState]);

  const submitSendQuestionnaires = handleSubmit(() => {
    void performSendQuestionnaires();
  });

  const performSendQuestionnaires = useCallback(async () => {
    const values = getValues();
    if (!patientReference || !organizationId || !practitionerRoleReference) {
      toast.error(
        "Dados de organização, profissional ou paciente incompletos.",
      );
      return;
    }
    if (!values.selectedQuestionnaireIds.length) {
      toast.error("Selecione ao menos um questionário.");
      return;
    }

    const selectedPayloads = questionnaires
      .filter((q) => q.id && values.selectedQuestionnaireIds.includes(q.id))
      .filter(isQuestionnaireSelectableForClinicalUse);

    if (!selectedPayloads.length) {
      toast.error(
        "Nenhum questionário válido para envio (rascunhos não são enviados).",
      );
      return;
    }

    try {
      await sendQuestionnaireTasks({
        organizationId,
        practitionerRoleReference,
        patientReference,
        ...(String(appointmentReference ?? "").trim()
          ? { appointmentReference: String(appointmentReference).trim() }
          : {}),
        questionnaires: selectedPayloads,
      });
      toast.success("Questionários enviados ao paciente.");
      setConfirmSendOpen(false);
      reset({ selectedQuestionnaireIds: [] });
      resetSendState();
      setUsageTab("all");
      onClose();
    } catch {
      /* erro já mapeado em sendError / toast opcional */
    }
  }, [
    appointmentReference,
    getValues,
    onClose,
    organizationId,
    patientReference,
    practitionerRoleReference,
    questionnaires,
    reset,
    resetSendState,
    sendQuestionnaireTasks,
  ]);

  const handleMainDialogClose = useCallback(
    (_event: unknown, _reason?: "backdropClick" | "escapeKeyDown") => {
      if (isSending) return;
      handleClose();
    },
    [handleClose, isSending],
  );

  const handleConfirmDialogClose = useCallback(
    (_event: unknown, _reason?: "backdropClick" | "escapeKeyDown") => {
      if (isSending) return;
      setConfirmSendOpen(false);
    },
    [isSending],
  );

  const handleWhatsappPromptClose = useCallback(
    (_event: unknown, _reason?: "backdropClick" | "escapeKeyDown") => {
      handleDismissWhatsappPrompt();
    },
    [handleDismissWhatsappPrompt],
  );

  const formattedPatientName = formatPatientGreetingName(patientName);

  return (
    <>
      <Dialog
        open={open && !whatsappPromptOpen}
        onClose={handleMainDialogClose}
        fullWidth
        maxWidth="md"
        aria-labelledby="send-questionnaires-title"
        disableEscapeKeyDown={isSending}
      >
        <DialogTitle id="send-questionnaires-title">
          Enviar questionários ao paciente
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {!canSend && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {scheduleAllowedUsage !== null &&
              scheduleAllowedUsage.length === 0
                ? "Envio de questionários não está disponível para este status de consulta (ex.: cancelada)."
                : "Não é possível enviar: verifique paciente, organização e perfil profissional."}
            </Alert>
          )}
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
          {sendError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {sendError}
            </Alert>
          )}

          <Box
            sx={{
              px: 1,
              py: 1.5,
              mb: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.info.main, 0.06),
              border: "1px dashed",
              borderColor: alpha(theme.palette.info.main, 0.35),
              width: "100%",
            }}
          >
            <Typography
              variant="body1"
              color="text.secondary"
              textAlign="center"
            >
              <Box component="span" fontWeight={700} color="text.primary">
                Configure o FluxZap
              </Box>{" "}
              para enviar questionários automaticamente por WhatsApp.{" "}
              <Link
                component={RouterLink}
                to={hasFluxZapExtension ? "/fluxzap" : "/extensions"}
                state={
                  hasFluxZapExtension ? undefined : { highlightFluxZap: true }
                }
                onClick={handleClose}
                sx={fluxZapLinkSx}
              >
                Clique aqui
              </Link>{" "}
              para {hasFluxZapExtension ? "configurar" : "ativar"}.
            </Typography>
          </Box>

          {visibleUsageTabs.length > 0 ? (
            <Tabs
              value={usageTab}
              onChange={(_, value: UsageTabValue) => setUsageTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
            >
              {visibleUsageTabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  label={tab.label}
                  value={tab.value}
                  {...a11yProps(index)}
                />
              ))}
            </Tabs>
          ) : null}

          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : filteredQuestionnaires.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Nenhum questionário nesta categoria.
            </Typography>
          ) : (
            <FormGroup>
              <List dense disablePadding>
                {filteredQuestionnaires.map((questionnaire) => {
                  const qid = questionnaire.id;
                  if (!qid) return null;
                  const checked = selectedQuestionnaireIds.includes(qid);
                  return (
                    <ListItem key={qid} disablePadding>
                      <ListItemButton
                        dense
                        onClick={() => toggleQuestionnaireId(qid)}
                      >
                        <ListItemIcon sx={{ minWidth: 42 }}>
                          <Checkbox
                            edge="start"
                            checked={checked}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{
                              "aria-labelledby": `send-q-label-${qid}`,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          id={`send-q-label-${qid}`}
                          primary={
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                              component="span"
                            >
                              <Typography component="span" variant="body1">
                                {questionnaire.title}
                              </Typography>
                              {questionnaire.status === "unknown" ? (
                                <Tooltip
                                  title={QUESTIONNAIRE_UNKNOWN_STATUS_TOOLTIP}
                                  arrow
                                >
                                  <ErrorOutlineIcon
                                    sx={{
                                      fontSize: 22,
                                      color: "error.main",
                                      display: "block",
                                    }}
                                    aria-label="Informação sobre versão vista pelos pacientes"
                                  />
                                </Tooltip>
                              ) : null}
                            </Stack>
                          }
                          secondaryTypographyProps={{ component: "div" }}
                          secondary={
                            <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                              {questionnaire.description?.trim() ? (
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {questionnaire.description}
                                </Typography>
                              ) : null}
                              {questionnaire.usageType ===
                              "postConsultation" ? (
                                <QuestionnaireProtocolLinkChips
                                  protocolNames={getProtocolNamesForQuestionnaire(
                                    qid,
                                  )}
                                  status={protocolIndexStatus}
                                />
                              ) : null}
                            </Stack>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </FormGroup>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            gap: 1,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              flex: 1,
              textAlign: { xs: "center", sm: "left" },
              mr: { sm: 1 },
            }}
          >
            Este questionário será enviado ao paciente.
            Informe-o que a resposta deve ser feita pelo Fluxmed (via Web ou Aplicativo).
          </Typography>
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button onClick={handleClose} color="inherit" disabled={isSending}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                void submitSendQuestionnaires();
              }}
              disabled={!canSend || isSending || isLoading}
              startIcon={
                isSending ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <Assignment />
                )
              }
            >
              Enviar
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmSendOpen}
        onClose={handleConfirmDialogClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="send-questionnaires-confirm-title"
        aria-describedby="send-questionnaires-confirm-desc"
        disableEscapeKeyDown={isSending}
      >
        <DialogTitle id="send-questionnaires-confirm-title">
          Enviar ao paciente
        </DialogTitle>
        <DialogContent>
          <Typography
            id="send-questionnaires-confirm-desc"
            variant="subtitle2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            Este questionário será enviado para o App Fluxmed do paciente.
          </Typography>

          <Divider
            sx={{ my: 2, borderColor: alpha(theme.palette.divider, 0.8) }}
          />

          <Typography
            variant="subtitle2"
            color="text.secondary"
            lineHeight={1.6}
          >
            Solicite ao paciente acessar o aplicativo Web em{" "}
            <Link
              href={FLUXMED_APP_URL}
              target="_blank"
              rel="noopener noreferrer"
              sx={fluxZapLinkSx}
            >
              {FLUXMED_APP_URL}
            </Link>
            , ou baixar nas lojas de aplicativos do celular para responder o
            questionário
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmSendOpen(false)}
            color="inherit"
            disabled={isSending}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              void performSendQuestionnaires();
            }}
            disabled={isSending}
            startIcon={
              isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
          >
            Ok
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={whatsappPromptOpen}
        onClose={handleWhatsappPromptClose}
        fullWidth
        maxWidth="sm"
        aria-labelledby="send-questionnaires-whatsapp-title"
        aria-describedby="send-questionnaires-whatsapp-desc"
      >
        <DialogTitle id="send-questionnaires-whatsapp-title">
          Questionários enviados
        </DialogTitle>
        <DialogContent>
          <Typography
            id="send-questionnaires-whatsapp-desc"
            variant="subtitle2"
            color="text.secondary"
            sx={{ lineHeight: 1.6 }}
          >
            Os questionários foram enviados para o App Fluxmed do paciente.
          </Typography>

          <Divider
            sx={{ my: 2, borderColor: alpha(theme.palette.divider, 0.8) }}
          />

          <Stack spacing={1.5}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              lineHeight={1.6}
            >
              Deseja enviar uma mensagem pelo WhatsApp Web para avisar{" "}
              <Box component="span" fontWeight={600} color="text.primary">
                {formattedPatientName}
              </Box>
              ?
            </Typography>

            <Box width="100%">
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                display="block"
                mb={0.75}
              >
                Modelo da mensagem
              </Typography>
              <Box
                sx={{
                  px: 1.5,
                  py: 1.25,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.15),
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  lineHeight={1.6}
                  sx={{ whiteSpace: "pre-wrap" }}
                >
                  {buildQuestionnaireWhatsAppMessage(patientName)}
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDismissWhatsappPrompt} color="inherit">
            Agora não
          </Button>
          <Button
            variant="contained"
            onClick={handleSendWhatsappAndClose}
            startIcon={<WhatsApp />}
            sx={{ color: "neutral0.main" }}
          >
            Enviar no WhatsApp Web
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});
